import React from 'react';
import { useLocalization } from '../engine/localization/context';
import { CheckCircle2, Star } from 'lucide-react';

export type PricingTier = 'starter' | 'business' | 'premium';

export interface PricingCardProps {
  title: string;
  subtitle?: string;
  tierName?: string;
  price: string;
  priceSuffix?: string;
  setupFeeLabel?: string;
  renewalPrice?: string;
  badge?: string;
  features: string[];
  highlight?: boolean;       // Business tier — strongest visual
  tier?: PricingTier;        // Controls accent color system
  ctaLabel: string;
  onCtaClick: () => void;
  animationDelay?: number;
  packageId?: string;
}

/**
 * Premium Pricing Card — Visual Polish Pass 2
 *
 * Tier color system:
 *   starter  → cyan accent
 *   business → cyan/violet border glow, elevated, badge
 *   premium  → violet/pink accent
 *
 * Respects prefers-reduced-motion via CSS utilities.
 */
export const PricingCard: React.FC<PricingCardProps> = ({
  title,
  subtitle,
  tierName,
  price,
  priceSuffix,
  setupFeeLabel,
  renewalPrice,
  badge,
  features,
  highlight = false,
  tier = 'starter',
  ctaLabel,
  onCtaClick,
  animationDelay: _animationDelay = 0,
  packageId,
}) => {
  const { direction, locale } = useLocalization();
  const isAr = locale === 'ar' || direction === 'rtl';

  // ── accent color system ──────────────────────────────────────
  const accentConfig: Record<PricingTier, {
    border: string;
    borderHover: string;
    shadow: string;
    shadowHover: string;
    priceColor: string;
    checkColor: string;
    ctaClass: string;
    bgGradient: string;
    badgeBg: string;
    dot: string;
  }> = {
    starter: {
      border: 'border-sky-500/30',
      borderHover: 'hover:border-sky-400/60',
      shadow: '',
      shadowHover: '',
      priceColor: 'text-sky-300',
      checkColor: 'text-sky-400',
      ctaClass: 'bg-slate-700/80 hover:bg-sky-500/20 hover:text-sky-300 border border-slate-600/60 hover:border-sky-500/40 text-slate-200',
      bgGradient: 'from-slate-900/60 to-slate-900/40',
      badgeBg: 'bg-sky-500',
      dot: 'bg-sky-500',
    },
    business: {
      border: 'border-sky-400/70',
      borderHover: 'hover:border-sky-300',
      shadow: 'shadow-[0_0_35px_rgba(56,189,248,0.25),0_0_70px_rgba(139,92,246,0.18),0_0_0_1px_rgba(56,189,248,0.25)]',
      shadowHover: 'hover:shadow-[0_0_55px_rgba(56,189,248,0.40),0_0_95px_rgba(139,92,246,0.25),0_0_0_1px_rgba(56,189,248,0.35)]',
      priceColor: 'text-sky-200',
      checkColor: 'text-sky-300',
      ctaClass: 'bg-gradient-to-r from-sky-500 to-violet-500 hover:from-sky-400 hover:to-violet-400 text-white shadow-[0_4px_25px_rgba(56,189,248,0.40)] hover:shadow-[0_6px_35px_rgba(56,189,248,0.55)]',
      bgGradient: 'from-sky-950/50 via-slate-900/80 to-violet-950/40',
      badgeBg: 'bg-gradient-to-r from-sky-500 to-violet-500',
      dot: 'bg-gradient-to-br from-sky-400 to-violet-400',
    },
    premium: {
      border: 'border-violet-500/30',
      borderHover: 'hover:border-violet-400/60',
      shadow: '',
      shadowHover: '',
      priceColor: 'text-violet-300',
      checkColor: 'text-violet-400',
      ctaClass: 'bg-violet-600/20 hover:bg-violet-500/30 border border-violet-500/40 hover:border-violet-400/60 text-violet-200 hover:text-violet-100',
      bgGradient: 'from-slate-900/60 to-violet-950/20',
      badgeBg: 'bg-gradient-to-r from-violet-500 to-pink-500',
      dot: 'bg-violet-500',
    },
  };

  const acc = accentConfig[tier] ?? accentConfig.starter;

  const elevationClass = highlight
    ? 'scale-[1.03] sm:scale-[1.05] z-20 shadow-2xl'
    : 'z-10';

  const hoverClass = highlight
    ? 'hover:scale-[1.05] sm:hover:scale-[1.07]'
    : 'hover:scale-[1.02] hover:-translate-y-0.5';

  return (
    <div
      data-package-id={packageId || tier}
      id={`package-${packageId || tier}`}
      className={[
        'relative flex flex-col gap-5 p-6 sm:p-7 rounded-2xl',
        'bg-gradient-to-b', acc.bgGradient,
        'border', acc.border, acc.borderHover,
        acc.shadow, acc.shadowHover,
        highlight ? 'animate-pulse-glow' : '',
        elevationClass, hoverClass,
        'backdrop-blur-md transition-all duration-300',
      ].join(' ')}
      dir={direction}
    >
      {/* Highlight badge */}
      {badge && (
        <div className={`absolute -top-4 left-1/2 -translate-x-1/2 whitespace-nowrap inline-flex items-center gap-1.5 px-3 py-1 rounded-full ${acc.badgeBg} text-white text-[11px] font-bold tracking-wide shadow-lg`}>
          <Star className="w-3 h-3 fill-white shrink-0" />
          {badge}
        </div>
      )}

      {/* Title & Subtitle */}
      <div className="text-center mt-1">
        {tierName && (
          <span className="text-[11px] font-mono font-bold tracking-wider uppercase text-slate-400 block mb-1">
            {tierName}
          </span>
        )}
        <h3 className="text-base sm:text-lg font-bold text-white">{title}</h3>
        {subtitle && (
          <p className="text-xs text-slate-400 mt-1 leading-relaxed">{subtitle}</p>
        )}
      </div>

      {/* Price & Renewal */}
      <div className="text-center leading-none">
        <div className="text-[11px] text-slate-400 font-medium mb-1.5">
          {setupFeeLabel || (isAr ? 'رسوم التأسيس' : 'Setup')}
        </div>
        <div className="flex items-baseline justify-center gap-1.5">
          <span className={`text-4xl sm:text-5xl font-black tracking-tight ${acc.priceColor}`}>
            {price}
          </span>
          {priceSuffix && (
            <span className="text-xs text-slate-400 font-medium">{priceSuffix}</span>
          )}
        </div>
        {renewalPrice && (
          <div className="text-xs text-slate-300/90 font-medium mt-3 pt-2.5 border-t border-slate-700/50 flex items-center justify-center gap-1">
            <span className="text-slate-400">{isAr ? 'التجديد السنوي:' : 'Annual renewal:'}</span>
            <span className="font-bold text-slate-200">{renewalPrice}</span>
          </div>
        )}
      </div>

      {/* Divider */}
      <div className={`h-px w-full ${highlight ? 'bg-gradient-to-r from-sky-500/30 via-violet-500/30 to-transparent' : 'bg-slate-700/60'}`} />

      {/* Feature list */}
      <ul className="flex flex-col gap-2.5 text-sm text-slate-300 flex-1">
        {features.map((feat, idx) => (
          <li key={idx} className="flex items-start gap-2.5">
            <CheckCircle2
              className={`w-4 h-4 flex-shrink-0 mt-0.5 ${acc.checkColor}`}
            />
            <span>{feat}</span>
          </li>
        ))}
      </ul>

      {/* CTA */}
      <button
        type="button"
        onClick={onCtaClick}
        className={`w-full py-2.5 rounded-xl font-bold text-sm transition-all duration-200 active:scale-[0.97] cursor-pointer ${acc.ctaClass}`}
      >
        {ctaLabel}
      </button>
    </div>
  );
};
